import { type FC, useEffect, useState } from 'react';
import { Flex, Form } from 'antd';
import { red } from '@ant-design/colors';
import { Button, DatePicker, Modal, Select } from '@g2u/ui-kit';
import { format, isBefore, isSameDay, startOfDay } from 'date-fns';

import { validateFormSilently } from 'helpers';
import { timeToDecimal } from 'pages/technician/edit/helpers/availability';
import styles from './technician-recurring-availability-modal.module.scss';

type Props = {
  isOpen: boolean;
  initialValues?: RecurringAvailabilityModal.InitialValues;
  isLoading: boolean;
  onSubmit: (values: RecurringAvailabilityModal.Payload) => void;
  onClose: () => void;
};

type FormFields = {
  startDate?: Date;
  endDate?: Date;
  days?: string[];
  startTime?: string;
  endTime?: string;
  setting?: 'N' | 'C';
};

const RECURRING_DAYS = [
  { code: 'mon', label: 'Monday' },
  { code: 'tue', label: 'Tuesday' },
  { code: 'wed', label: 'Wednesday' },
  { code: 'thu', label: 'Thursday' },
  { code: 'fri', label: 'Friday' },
  { code: 'sat', label: 'Saturday' },
  { code: 'sun', label: 'Sunday' },
];

const HALF_HOURS = Array.from({ length: 25 }, (_, index) => {
  const minutes = 8 * 60 + index * 30;

  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${minutes % 60 ? '30' : '00'}`;
});

const STARTING_TIME_OPTIONS = HALF_HOURS.slice(0, -1).map((time) => ({ value: time, label: time }));
const ENDING_TIME_OPTIONS = HALF_HOURS.slice(1).map((time) => ({ value: time, label: time }));

const SETTING_OPTIONS = [
  { value: 'N', label: 'Unavailable' },
  { value: 'C', label: 'Requires Confirmation' },
];

const FIELD_WIDTH = 268;

const DATE_FORMAT = 'd MMM yyyy';

const isBeforeToday = (date: Date | null) => !!date && isBefore(date, startOfDay(new Date()));

const toApiDate = (date?: Date) => (date ? format(date, 'yyyy-MM-dd') : '');

const toDayCodes = (days?: string[]) =>
  RECURRING_DAYS.map((day) => day.code).filter((code) => days?.includes(code));

const toComparable = (values: FormFields) =>
  JSON.stringify([
    toApiDate(values.startDate),
    toApiDate(values.endDate),
    toDayCodes(values.days),
    values.startTime ? timeToDecimal(values.startTime) : null,
    values.endTime ? timeToDecimal(values.endTime) : null,
    values.setting ?? null,
  ]);

const RecurringDaysButtons: FC<{ value?: string[]; onChange?: (days: string[]) => void }> = ({ value = [], onChange }) => (
  <Flex gap={8} align="flex-start">
    <span className={styles.recurringModal__requiredMark} style={{ color: red[4] }}>
      *
    </span>
    {RECURRING_DAYS.map((day) => {
      const isSelected = value.includes(day.code);

      return (
        <Button.$
          key={day.code}
          type={Button.Type.Default}
          className={isSelected ? styles.recurringModal__day_selected : undefined}
          aria-pressed={isSelected}
          onClick={() => onChange?.(isSelected ? value.filter((code) => code !== day.code) : [...value, day.code])}
          dataTestId={`recurring-day-${day.code}`}
        >
          {day.label}
        </Button.$>
      );
    })}
  </Flex>
);

export const RecurringAvailabilityModal: FC<Props> = ({ isOpen, initialValues, isLoading, onSubmit, onClose }) => {
  const [form] = Form.useForm<FormFields>();
  const [isFormValid, setIsFormValid] = useState(false);
  const startDate = Form.useWatch('startDate', form);
  const values = Form.useWatch([], form);
  const isEditing = !!initialValues;
  const isChanged = !isEditing || toComparable(values ?? {}) !== toComparable(initialValues);

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setIsFormValid(false);

      if (initialValues) {
        validateFormSilently(form, setIsFormValid);
      }
    }
  }, [isOpen, initialValues, form]);

  const handleFormChange = (changedValues: FormFields) => {
    if ('startDate' in changedValues && form.isFieldTouched('endDate')) {
      form.validateFields(['endDate']).catch(() => {});
    }
    if ('startTime' in changedValues && form.isFieldTouched('endTime')) {
      form.validateFields(['endTime']).catch(() => {});
    }
    validateFormSilently(form, setIsFormValid);
  };

  const handleFinish = (values: FormFields) => {
    onSubmit({
      start_date: toApiDate(values.startDate),
      end_date: toApiDate(values.endDate),
      start_time: timeToDecimal(values.startTime!),
      end_time: timeToDecimal(values.endTime!),
      days: toDayCodes(values.days),
      setting: values.setting!,
    });
  };

  return (
    <Modal.$
      dataTestId="technician-recurring-availability-modal"
      open={isOpen}
      title={isEditing ? 'Edit Recurring Availability' : 'Add Recurring Availability'}
      width={924}
      okText={isEditing ? 'Save' : 'Add'}
      cancelText="Cancel"
      onOk={form.submit}
      onCancel={onClose}
      cancelButtonProps={{ disabled: isLoading }}
      okButtonProps={{ disabled: !isFormValid || !isChanged || isLoading, loading: isLoading, style: { width: 120 } }}
    >
      <Form
        form={form}
        initialValues={initialValues}
        layout="vertical"
        preserve={false}
        disabled={isLoading}
        onValuesChange={handleFormChange}
        onFinish={handleFinish}
      >
        <div className={styles.recurringModal__section}>Effective Period</div>
        <Flex gap={16}>
          <DatePicker.$
            label="Starting Date"
            name="startDate"
            colon
            required
            rules={[{ required: true, message: 'Starting Date is required.' }]}
            format={DATE_FORMAT}
            disabledDate={(date) => isBeforeToday(date) && !(date && initialValues && isSameDay(date, initialValues.startDate))}
            className={styles.recurringModal__field}
          />
          <DatePicker.$
            label="Ending Date"
            name="endDate"
            colon
            required
            rules={[
              { required: true, message: 'Ending Date is required.' },
              {
                validator: (_, value?: Date) =>
                  value && form.getFieldValue('startDate') && isBefore(startOfDay(value), startOfDay(form.getFieldValue('startDate')))
                    ? Promise.reject(new Error('Ending Date can not be before the Starting Date.'))
                    : Promise.resolve(),
              },
            ]}
            format={DATE_FORMAT}
            disabledDate={(date) => isBeforeToday(date) || (!!date && !!startDate && isBefore(startOfDay(date), startOfDay(startDate)))}
            className={styles.recurringModal__field}
          />
        </Flex>
        <div className={styles.recurringModal__section}>Recurring Days</div>
        <Form.Item
          name="days"
          rules={[{ required: true, type: 'array', min: 1, message: 'Select at least one recurring day.' }]}
        >
          <RecurringDaysButtons />
        </Form.Item>
        <div className={styles.recurringModal__section}>Daily Timing</div>
        <Flex gap={16}>
          <Select.$
            label="Starting Time"
            name="startTime"
            colon
            required
            placeholder="Select time"
            width={FIELD_WIDTH}
            rules={[{ required: true, message: 'Starting Time is required.' }]}
            options={STARTING_TIME_OPTIONS}
          />
          <Select.$
            label="Ending Time"
            name="endTime"
            colon
            required
            placeholder="Select time"
            width={FIELD_WIDTH}
            rules={[
              { required: true, message: 'Ending Time is required.' },
              {
                validator: (_, value?: string) =>
                  value && form.getFieldValue('startTime') && timeToDecimal(value) <= timeToDecimal(form.getFieldValue('startTime'))
                    ? Promise.reject(new Error('Ending Time must be after the Starting Time.'))
                    : Promise.resolve(),
              },
            ]}
            options={ENDING_TIME_OPTIONS}
          />
        </Flex>
        <div className={styles.recurringModal__section}>Status</div>
        <Select.$
          label="Setting"
          name="setting"
          colon
          required
          placeholder="Select"
          width={FIELD_WIDTH}
          rules={[{ required: true, message: 'Setting is required.' }]}
          options={SETTING_OPTIONS}
        />
      </Form>
    </Modal.$>
  );
};

export namespace RecurringAvailabilityModal {
  export type InitialValues = {
    startDate: Date;
    endDate?: Date;
    days: string[];
    startTime: string;
    endTime: string;
    setting: 'N' | 'C';
  };

  export type Payload = {
    start_date: string;
    end_date: string;
    start_time: number;
    end_time: number;
    days: string[];
    setting: 'N' | 'C';
  };
}
